import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO } from "../hooks/useSEO";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Services() {
  const { t, lang } = useLanguage();
  const [services, setServices] = useState(null);

  useSEO(
    lang === "nl"
      ? "Diensten & Prijzen | EliteTheBarber Kapper Utrecht"
      : "Services & Pricing | EliteTheBarber Barber Utrecht",
    lang === "nl"
      ? "Bekijk alle kappersdiensten en prijzen bij EliteTheBarber in Utrecht: knippen, baard trimmen, contouren en meer. Boek direct online je afspraak."
      : "View all barber services and prices at EliteTheBarber in Utrecht: haircuts, beard trimming, line-ups and more. Book your appointment online instantly."
  );

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  return (
    <div data-testid="services-page" className="py-20 sm:py-28 px-4 sm:px-6 min-h-[60vh]">
      <div className="max-w-5xl mx-auto">
        <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3">{t("nav.services")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-4 tracking-tight">{t("services.title")}</h1>
        <p className="text-white/60 text-base mb-12 max-w-lg">{t("services.subtitle")}</p>

        {services === null && <p className="text-white/50">{t("common.loading")}</p>}

        {services && services.length === 0 && (
          <div data-testid="services-empty-state" className="bg-[#141414] border border-white/5 rounded-xl p-10 text-center">
            <p className="text-gold text-lg mb-2">{t("services.comingSoonTitle")}</p>
            <p className="text-white/60">{t("services.comingSoonText")}</p>
          </div>
        )}

        {services && services.length > 0 && (
          <div className="divide-y divide-white/5 border-t border-b border-white/5">
            {services.map((s) => (
              <div key={s.id} data-testid={`services-page-row-${s.id}`} className="flex items-center justify-between gap-6 py-6">
                {s.image_url && (
                  <img src={`${BACKEND_URL}${s.image_url}`} alt={lang === "nl" ? s.name_nl : s.name_en} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-white text-lg sm:text-xl font-serif mb-1">{lang === "nl" ? s.name_nl : s.name_en}</h3>
                  {(lang === "nl" ? s.description_nl : s.description_en) && (
                    <p className="text-white/50 text-sm mb-1">{lang === "nl" ? s.description_nl : s.description_en}</p>
                  )}
                  <p className="flex items-center gap-1.5 text-white/40 text-xs"><Clock size={12} /> {s.duration} {t("services.minutesLabel")}</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className="text-gold text-xl font-serif">€{s.price % 1 === 0 ? s.price.toFixed(0) : s.price}</span>
                  <Link to={`/boeken?service=${s.id}`} data-testid={`services-page-book-${s.id}`} className="text-xs text-white/70 hover:text-gold underline underline-offset-4">
                    {t("services.bookThis")}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
